/**
 * Operations guide (owner playbook). Search matches title, summary, optional body, bullets, keywords, category.
 */

export type HelpCenterCategoryId =
  | "gettingStarted"
  | "dailyOperations"
  | "troubleshooting"
  | "printerHardware";

export type HelpCenterRouteKey =
  | "restaurantSetting"
  | "employee"
  | "customers"
  | "menu"
  | "modifiers"
  | "combos"
  | "orders"
  | "financeTransactions"
  | "financeCashDrawer"
  | "financePayroll"
  | "promotions"
  | "restaurantTable"
  | "reportsOverview"
  | "reportsSales"
  | "reportsPayments"
  | "reportsEmployees"
  | "reportsTax"
  | "events";

export type HelpCenterCategory = {
  id: HelpCenterCategoryId;
  label: string;
  tabLabel: string;
  description: string;
};

export type ManualSection = {
  id: string;
  category: HelpCenterCategoryId;
  title: string;
  /** One line on the card face */
  summary: string;
  keywords: string[];
  /** Optional extra line when expanded (usually empty—use bullets only) */
  body?: string;
  /** Concise instruction bullets */
  bullets: string[];
  relatedRouteKeys?: HelpCenterRouteKey[];
};

export const HELP_CENTER_CATEGORIES: HelpCenterCategory[] = [
  {
    id: "gettingStarted",
    label: "Getting started",
    tabLabel: "Getting started",
    description: "Launch checklist: store profile, menu, team, printers, then the POS app.",
  },
  {
    id: "dailyOperations",
    label: "Daily operations",
    tabLabel: "Daily ops",
    description: "Run service, money, tables, and reports from the owner app.",
  },
  {
    id: "troubleshooting",
    label: "Troubleshooting",
    tabLabel: "Fix issues",
    description: "Fast paths for payments, sync, and sign-in problems.",
  },
  {
    id: "printerHardware",
    label: "Printer & hardware",
    tabLabel: "Hardware",
    description: "Receipts, kitchen tickets, and print settings in sync with the floor.",
  },
];

export function categoryLabel(id: HelpCenterCategoryId): string {
  return HELP_CENTER_CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

export const MANUAL_SECTIONS: ManualSection[] = [
  {
    id: "owner-web-overview",
    category: "gettingStarted",
    title: "Set up your store",
    summary: "Confirm store defaults before you open.",
    keywords: ["dashboard", "browser", "web", "owner app", "sidebar", "settings", "hours"],
    bullets: [
      "Open Settings: profile, hours, delivery, address/tax as needed.",
      "Add menu and employees before go-live.",
      "Reports, Promotions, Events only if those features are on for the store.",
    ],
    relatedRouteKeys: ["restaurantSetting", "menu", "employee", "reportsOverview"],
  },
  {
    id: "add-menu-items",
    category: "gettingStarted",
    title: "Add menu items",
    summary: "Structure matches what staff ring in.",
    keywords: ["menu", "categories", "items", "modifiers", "combos", "price"],
    bullets: [
      "Create categories, then items (price, photo, availability).",
      "Add modifiers for required/optional choices.",
      "Use Combos for fixed bundles.",
      "After big edits, confirm one item on the POS before service.",
    ],
    relatedRouteKeys: ["menu", "modifiers", "combos"],
  },
  {
    id: "desktop-pos-install",
    category: "gettingStarted",
    title: "Install the POS app",
    summary: "Desktop client on the station that rings and prints.",
    keywords: ["download", "install", "desktop", "pos", "windows", "mac", "client"],
    bullets: [
      "Install on order-taking / receipt stations only.",
      "Sign in; confirm the correct restaurant.",
      "Run one test order + test print before opening.",
      "Restart client after large menu or settings pushes if the floor looks stale.",
    ],
    relatedRouteKeys: ["restaurantSetting"],
  },
  {
    id: "setup-printers",
    category: "gettingStarted",
    title: "Set up printers",
    summary: "Owner print rules + physical printers on each station.",
    keywords: ["receipt", "kitchen", "printer", "usb", "ethernet", "print tab"],
    bullets: [
      "Set receipt / language options in Settings (owner).",
      "Pick the correct printer in Windows/macOS on each station.",
      "Test print after cable, network, or driver changes.",
    ],
    relatedRouteKeys: ["restaurantSetting"],
  },
  {
    id: "staff-and-permissions",
    category: "gettingStarted",
    title: "Add employees",
    summary: "Invites and roles match who does what.",
    keywords: ["staff", "roles", "permissions", "invite", "employees", "manager", "access"],
    bullets: [
      "Invite from Employees; finish onboarding per person.",
      "Limit finance / menu / reports access to who needs it.",
      "Match desktop PIN / permissions to your floor policy.",
    ],
    relatedRouteKeys: ["employee"],
  },
  {
    id: "menu-management",
    category: "dailyOperations",
    title: "Update menu, prices & combos",
    summary: "Keep POS and channels aligned.",
    keywords: ["menu", "categories", "items", "modifiers", "combos", "price", "availability"],
    bullets: [
      "Categories → items → modifiers → combos in that order when changing structure.",
      "Mark required modifiers so they cannot be skipped at checkout.",
      "Refresh or restart POS after major edits to verify the floor.",
    ],
    relatedRouteKeys: ["menu", "modifiers", "combos"],
  },
  {
    id: "orders-and-checks",
    category: "dailyOperations",
    title: "Take orders & payments",
    summary: "Live vs completed checks and search.",
    keywords: ["active", "completed", "orders", "checks", "checkout", "payment", "search"],
    bullets: [
      "Active: open tickets; Completed: past checks.",
      "Search by order id, phone, name, or email.",
      "Payment questions: start here, then Payments if needed.",
    ],
    relatedRouteKeys: ["orders", "financeTransactions"],
  },
  {
    id: "customers",
    category: "dailyOperations",
    title: "Review customer records",
    summary: "Guest context next to orders.",
    keywords: ["customers", "guest", "crm", "phone", "email", "contact"],
    bullets: [
      "Use for contact / repeat-guest context.",
      "Transaction detail: Orders first; Customers for the person.",
    ],
    relatedRouteKeys: ["customers", "orders"],
  },
  {
    id: "finance-workflow",
    category: "dailyOperations",
    title: "Handle money, refunds & payroll",
    summary: "Pick the right Finance screen.",
    keywords: ["finance", "payments", "cash drawer", "payroll", "tips", "charges", "refunds"],
    bullets: [
      "Payments: card charges, refunds, settlement questions.",
      "Cash Drawer: till drops, payouts, shift cash.",
      "Payroll: labor and tips over a period (then your payroll provider).",
    ],
    relatedRouteKeys: ["financeTransactions", "financeCashDrawer", "financePayroll"],
  },
  {
    id: "settings-and-store-controls",
    category: "dailyOperations",
    title: "Update store settings",
    summary: "Store-wide behavior lives here.",
    keywords: ["settings", "hours", "delivery", "terminal", "profile", "language", "subscription"],
    bullets: [
      "Update hours before holidays or special hours.",
      "Delivery / messaging / language changes after you change how you operate.",
      "Adjust print-related options before blaming hardware.",
    ],
    relatedRouteKeys: ["restaurantSetting"],
  },
  {
    id: "tables-and-floor",
    category: "dailyOperations",
    title: "Manage tables & floor",
    summary: "Layout and dine-in flow.",
    keywords: ["tables", "floor plan", "layout", "dine in", "table status"],
    bullets: [
      "Name tables the way the floor speaks.",
      "Layout for fast tap selection during rush.",
      "Retest guest-order links after you change the map.",
    ],
    relatedRouteKeys: ["restaurantTable"],
  },
  {
    id: "reports-workflow",
    category: "dailyOperations",
    title: "Close the day with reports",
    summary: "Match report to the question.",
    keywords: ["reports", "overview", "sales", "payments", "employees", "tax", "analytics"],
    bullets: [
      "Overview: quick KPI snapshot.",
      "Sales: revenue by channel / type / category.",
      "Payments / Employees / Tax: use when that is the actual question.",
    ],
    relatedRouteKeys: ["reportsOverview", "reportsSales", "reportsPayments", "reportsEmployees", "reportsTax"],
  },
  {
    id: "promotions-and-events",
    category: "dailyOperations",
    title: "Run promotions and events carefully",
    summary: "Dates and eligibility must be right.",
    keywords: ["promotions", "discounts", "events", "campaign", "special event", "date range"],
    bullets: [
      "Confirm start/end and who qualifies before go-live.",
      "Align events with hours, menu, and staffing.",
      "Features must be enabled for your store to see these areas.",
    ],
    relatedRouteKeys: ["promotions", "events"],
  },
  {
    id: "desktop-home-modules",
    category: "dailyOperations",
    title: "Desktop POS — Home screen",
    summary: "Launcher tiles from the EatsBits desktop app (JavaFX).",
    keywords: [
      "desktop",
      "pos",
      "launcher",
      "dine in",
      "pick up",
      "delivery",
      "orders",
      "clock",
      "cash drawer",
      "printers",
      "in house",
      "channel",
      "register",
    ],
    bullets: [
      "Dine In → table map for dine-in service.",
      "Pick Up or Delivery → choose a channel (In House first, then store-configured channels).",
      "Orders → in-app order list (open and completed checks).",
      "Clock → time clock for staff.",
      "Cash Drawer → in-store drawer tools; tile appears only when a server is signed in and the role is not employee-only.",
      "Printers → printer setup / selection; tile is for owner or manager roles (hidden for employee-only roles).",
      "Top logo → Home; on inner screens the header control is Back (one step).",
    ],
  },
  {
    id: "desktop-order-drawer-tools",
    category: "dailyOperations",
    title: "Desktop POS — order tools",
    summary: "Actions on the order details strip.",
    keywords: ["desktop", "pos", "split", "print", "pay", "order details", "check", "void"],
    bullets: [
      "On an open check, use Split, Print, and Pay from the order details row when shown.",
      "Completed checks may still offer Print and payment recap where applicable.",
      "Which buttons appear depends on ticket state and staff permissions.",
    ],
  },
  {
    id: "payments-troubleshooting",
    category: "troubleshooting",
    title: "Payment failed or pending",
    summary: "Device and network first.",
    keywords: ["declined", "failed", "pending", "reader", "terminal", "payment error"],
    bullets: [
      "Check reader power, pairing, and station network.",
      "See the attempt in Payments before many blind retries.",
      "Escalate with order id + time if it repeats.",
    ],
    relatedRouteKeys: ["financeTransactions", "orders"],
  },
  {
    id: "sync-troubleshooting",
    category: "troubleshooting",
    title: "Orders or menu not syncing",
    summary: "Owner vs POS mismatch.",
    keywords: ["sync", "stale", "reload", "refresh", "desktop", "menu not updating"],
    bullets: [
      "Confirm POS is online and on the correct store.",
      "Restart or refresh POS after big menu pushes.",
      "One item wrong: check it is active and in the right category/modifiers.",
    ],
    relatedRouteKeys: ["menu", "restaurantSetting"],
  },
  {
    id: "access-troubleshooting",
    category: "troubleshooting",
    title: "PIN, login, or permissions",
    summary: "Auth vs role limits.",
    keywords: ["pin", "login", "sign in", "permissions", "manager", "locked out"],
    bullets: [
      "Wrong PIN vs role not allowed to void / discount / drawer.",
      "Re-check Employees if duties changed.",
    ],
    relatedRouteKeys: ["employee"],
  },
  {
    id: "receipt-and-kitchen-printers",
    category: "printerHardware",
    title: "Receipt and kitchen printers",
    summary: "Cloud intent + local hardware must match.",
    keywords: ["receipt", "kitchen ticket", "printer", "usb", "ethernet", "driver"],
    bullets: [
      "Owner sets what should print; OS/station picks where it prints.",
      "Test print after cable, driver, or network changes.",
      "Wrong output: check paper width and vendor cut/align settings.",
    ],
    relatedRouteKeys: ["restaurantSetting"],
  },
  {
    id: "owner-print-settings",
    category: "printerHardware",
    title: "Owner app print settings",
    summary: "Receipt behavior from the cloud.",
    keywords: ["print tab", "receipt language", "owner settings", "printer settings"],
    bullets: [
      "Configure receipt / language in Settings.",
      "If cloud is right but the wrong printer fires, fix the station printer selection.",
      "Retest after language or routing changes.",
    ],
    relatedRouteKeys: ["restaurantSetting"],
  },
];

export function getSectionsForCategory(categoryId: HelpCenterCategoryId): ManualSection[] {
  return MANUAL_SECTIONS.filter((section) => section.category === categoryId);
}

export function sectionSearchText(section: ManualSection): string {
  const category = categoryLabel(section.category).toLowerCase();
  return [
    section.title,
    section.summary,
    section.keywords.join(" "),
    section.body ?? "",
    section.bullets.join(" "),
    category,
  ]
    .join(" ")
    .toLowerCase();
}

export function sectionSearchScore(section: ManualSection, rawQuery: string): number {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return 0;

  let score = 0;
  if (section.title.toLowerCase().includes(query)) score += 7;
  if (section.summary.toLowerCase().includes(query)) score += 4;
  if (section.keywords.some((keyword) => keyword.toLowerCase().includes(query))) score += 5;
  if ((section.body ?? "").toLowerCase().includes(query)) score += 2;
  if (section.bullets.some((item) => item.toLowerCase().includes(query))) score += 2;
  if (categoryLabel(section.category).toLowerCase().includes(query)) score += 1;

  return score;
}
